import { useAppDispatch } from "@/store/hooks";
import { updateElementProps } from "@/store/slideEditorSlice";
import { buildRevealProps, getRevealedIndexes } from "@ai-teach/canvas-core";
import { BOX_SCALE_STEP, clampBoxScale } from "@ai-teach/canvas-styles";
import type { SpellingElement } from "@ai-teach/types";
import { useEffect, useState } from "react";
import { FaMinus, FaPlus } from "react-icons/fa";

interface SpellingElementToolbarProps {
  spellingElement: SpellingElement;
}

/**
 * Authoring controls for the spelling question: the target word, which of its letters are
 * pre-filled/locked for the student, and the letter box size. Box *count* is derived from the word
 * length elsewhere — there is nothing to configure for that here.
 *
 * Reveal is picked per letter rather than as a count, because which letters are missing is the
 * whole exercise — hiding the vowels of `butterfly` teaches something that hiding its first three
 * letters does not. Picking happens on these chips rather than on the canvas preview so it can't
 * fight the element's drag and resize handles.
 *
 * The size control exists because box size is otherwise auto-fitted to the frame's width, which a
 * long word shrinks past the point of legibility on a phone. Scaling up overflows the frame's
 * width on purpose: the row scrolls horizontally on both platforms (see getSpellingStyles), so a
 * long word runs off the edge at a readable size instead of fitting at an unreadable one.
 */
export function SpellingElementToolbar({ spellingElement }: SpellingElementToolbarProps) {
  const dispatch = useAppDispatch();
  const [localWord, setLocalWord] = useState(spellingElement.word);
  const boxScale = clampBoxScale(spellingElement.boxScale);

  useEffect(() => {
    setLocalWord(spellingElement.word);
  }, [spellingElement.word]);

  const commitWord = (value: string) => {
    dispatch(updateElementProps({ id: spellingElement.id, props: { word: value } }));
  };

  const revealedIndexes = getRevealedIndexes(spellingElement);
  // Revealing the last editable box would leave the student nothing to type, which every consumer
  // then reads as "nothing to grade" — the question would auto-submit as vacuously correct the
  // moment it rendered. Blocked here rather than absorbed downstream so the teacher sees why.
  const canRevealMore = revealedIndexes.size < spellingElement.word.length - 1;

  const handleToggleRevealed = (index: number) => {
    const next = new Set(revealedIndexes);
    if (!next.delete(index)) next.add(index);
    dispatch(updateElementProps({ id: spellingElement.id, props: buildRevealProps(next) }));
  };

  // Clamped here as well as in the style function: without it the stored value would drift past
  // the bounds on repeated clicks and the displayed percentage would stop matching what renders.
  const handleBoxScaleChange = (delta: number) => {
    dispatch(
      updateElementProps({
        id: spellingElement.id,
        props: { boxScale: clampBoxScale(boxScale + delta) },
      })
    );
  };

  return (
    <>
      {/* Word input */}
      <div className="flex items-center gap-1.5">
        <label htmlFor="spelling-word" className="text-xs font-semibold text-gray-500 uppercase">
          Word
        </label>
        <input
          id="spelling-word"
          type="text"
          value={localWord}
          onChange={(e) => setLocalWord(e.target.value)}
          onBlur={(e) => commitWord(e.target.value)}
          className="w-32 text-gray-700 px-2 py-1.5 border border-gray-300 rounded-lg text-xs font-medium focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50/50 transition-all"
        />
      </div>

      <div className="w-px h-6 bg-gray-300" />

      {/* Revealed letters — click a letter to pre-fill/lock it for the student */}
      <div className="flex items-center gap-1.5">
        <span className="text-xs font-semibold text-gray-500 uppercase">Reveal</span>
        <div className="flex items-center gap-1">
          {spellingElement.word.split("").map((letter, index) => {
            const isRevealed = revealedIndexes.has(index);
            const isDisabled = !isRevealed && !canRevealMore;
            return (
              <button
                // biome-ignore lint/suspicious/noArrayIndexKey: letters are a fixed-length sequence, no stable id
                key={`reveal-${index}`}
                type="button"
                disabled={isDisabled}
                onClick={() => handleToggleRevealed(index)}
                aria-pressed={isRevealed}
                title={
                  isDisabled
                    ? "At least one letter must stay blank 至少要留一格空白"
                    : `Reveal or hide letter ${index + 1}`
                }
                className={`w-6 h-6 rounded-md border text-xs font-semibold transition-colors ${
                  isRevealed
                    ? "bg-primary-500 border-primary-500 text-white"
                    : "bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100"
                } ${isDisabled ? "opacity-40 cursor-not-allowed" : ""}`}
              >
                {letter}
              </button>
            );
          })}
        </div>
      </div>

      <div className="w-px h-6 bg-gray-300" />

      {/* Letter box size */}
      <div className="flex items-center gap-1">
        <span className="text-xs font-semibold text-gray-500 uppercase">Box size</span>
        <button
          type="button"
          onClick={() => handleBoxScaleChange(-BOX_SCALE_STEP)}
          aria-label="Decrease letter box size"
          title="Decrease letter box size 縮小字母框"
          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600"
        >
          <FaMinus className="w-3 h-3" />
        </button>
        <span className="text-xs font-medium text-gray-500 w-10 text-center select-none">
          {Math.round(boxScale * 100)}%
        </span>
        <button
          type="button"
          onClick={() => handleBoxScaleChange(BOX_SCALE_STEP)}
          aria-label="Increase letter box size"
          title="Increase letter box size 放大字母框"
          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600"
        >
          <FaPlus className="w-3 h-3" />
        </button>
      </div>
    </>
  );
}
